import React, { useState } from 'react';
import axios from 'axios';
import { Trash2 } from 'lucide-react';
import { useAlert } from '../context/AlertContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Textarea } from './ui/textarea';

// Keep in step with MIN_DELETION_REASON_LENGTH / MAX_DELETION_REASON_LENGTH
// in backend/utils/deletion_reason.py — the backend is the real gate.
const MIN_REASON_LENGTH = 10;
const MAX_REASON_LENGTH = 1000;

const DELETE_MESSAGE =
  'The record is removed from all lists and dashboards. It is kept in the audit trail and can only be restored by an administrator.';

/**
 * True when deleting a record in this status needs a written reason:
 * every status except Draft. Pass no status for modules without a Draft
 * state (meetings, training records) — they always need one.
 */
export const deleteNeedsReason = (status) =>
  String(status || '').trim().toLowerCase() !== 'draft';

/**
 * Delete button shared by the safety-hub record pages (incidents,
 * observations, CAPA, HIRA, PTW, audits, meetings, SSEM, training, documents).
 *
 * The backend DELETE endpoints all soft-delete (is_deleted + deleted_at +
 * deleted_by, audit-logged); this component only asks for confirmation (and,
 * for non-draft records, a reason), calls the endpoint and reports the
 * result. Visibility is the caller's job — gate it on the module's
 * <resource>:delete permission. The UI gate is a convenience only: every
 * endpoint enforces RequirePermission and the reason rule server-side.
 *
 * Props:
 *   url           — full DELETE URL for the record
 *   label         — human name for the confirm text, e.g. "incident INC-2026-011"
 *   onDeleted     — called after a successful delete (navigate away / refresh)
 *   requireReason — ask for a reason (use deleteNeedsReason(record.status));
 *                   sent as the JSON body { reason } and stored in the audit log
 *   compact       — icon-only button for table rows
 */
const DeleteRecordButton = ({
  url,
  label,
  onDeleted,
  requireReason = false,
  compact = false,
  className = '',
}) => {
  const { confirm, showSuccess, showError } = useAlert();
  const [busy, setBusy] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState('');

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= MIN_REASON_LENGTH;

  const doDelete = async (reasonText) => {
    setBusy(true);
    try {
      await axios.delete(url, reasonText ? { data: { reason: reasonText } } : undefined);
      setReasonOpen(false);
      showSuccess(`Deleted ${label}.`);
      if (onDeleted) onDeleted();
    } catch (err) {
      showError(err?.response?.data?.detail || `Could not delete ${label}.`);
    } finally {
      setBusy(false);
    }
  };

  const handleClick = async (e) => {
    // Row buttons sit inside clickable rows; don't open the record.
    e.stopPropagation();
    if (requireReason) {
      setReason('');
      setReasonOpen(true);
      return;
    }
    const ok = await confirm(`Delete ${label}?`, DELETE_MESSAGE, {
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    await doDelete(null);
  };

  const reasonDialog = (
    <Dialog open={reasonOpen} onOpenChange={(open) => !busy && setReasonOpen(open)}>
      {/* Stop clicks from bubbling to a clickable table row behind the portal. */}
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{`Delete ${label}?`}</DialogTitle>
          <DialogDescription>{DELETE_MESSAGE}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <label htmlFor="delete-reason" className="text-sm font-medium text-gray-900">
            Reason for deletion <span className="text-red-600">*</span>
          </label>
          <Textarea
            id="delete-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={MAX_REASON_LENGTH}
            rows={4}
            placeholder="Why is this record being deleted?"
            autoFocus
          />
          <p className={`text-xs ${reason && !reasonValid ? 'text-red-600' : 'text-gray-500'}`}>
            {`At least ${MIN_REASON_LENGTH} characters (${trimmedReason.length}/${MAX_REASON_LENGTH}).`}
          </p>
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={() => setReasonOpen(false)}
            disabled={busy}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => doDelete(trimmedReason)}
            disabled={busy || !reasonValid}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (compact) {
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          disabled={busy}
          title={`Delete ${label}`}
          aria-label={`Delete ${label}`}
          className={`p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-50 ${className}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
        {reasonDialog}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        className={`inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 ${className}`}
      >
        <Trash2 className="w-4 h-4" />
        {busy ? 'Deleting…' : 'Delete'}
      </button>
      {reasonDialog}
    </>
  );
};

export default DeleteRecordButton;
